import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";

/**
 * Styles for the visual mode. Ships inside the visual compartment, so code
 * mode stays pixel-identical. Every color is a CSS var from the design system
 * (same contract as `cmTheme.ts`) — themes keep working without reconfigure.
 */
export const visualTheme: Extension = EditorView.theme({
  // A document, not a terminal: an A4-like sheet centered on a desk, with
  // proportional font and roomier line height. (Line numbers / fold gutter
  // are removed via gutterCompartment — the base theme pins gutters with
  // !important, CSS hiding does not stick.) `&.cm-editor` bumps specificity
  // above cmTheme's rules — CSS injection order between themes is not
  // guaranteed.
  "&.cm-editor": {
    backgroundColor: "color-mix(in srgb, var(--bg-0) 94%, var(--fg-3) 6%)",
  },
  "&.cm-editor .cm-scroller": {
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
    lineHeight: "1.78",
    backgroundColor: "color-mix(in srgb, var(--bg-0) 94%, var(--fg-3) 6%)",
    scrollPadding: "24px 0 72px",
  },
  "&.cm-editor .cm-content": {
    boxSizing: "border-box",
    width: "calc(100% - 48px)",
    maxWidth: "var(--visual-page-width, 816px)",
    minHeight: "calc(100% - 72px)",
    margin: "24px auto 64px",
    padding:
      "var(--visual-page-pad-top, 64px) var(--visual-page-pad-x, 72px) var(--visual-page-pad-bottom, 96px)",
    fontSize: "var(--visual-font-size, 15.5px)",
    color: "var(--fg-0)",
    backgroundColor: "var(--bg-1)",
    border: "1px solid var(--border-strong)",
    borderRadius: "10px",
    boxShadow: "var(--shadow-2)",
    transition: "border-color 140ms var(--ease), box-shadow 140ms var(--ease)",
  },
  "&.cm-focused .cm-content": {
    borderColor: "var(--accent-line)",
    boxShadow:
      "var(--shadow-2), 0 0 0 1px color-mix(in srgb, var(--accent) 8%, transparent)",
  },
  "&.cm-editor .cm-activeLine": {
    backgroundColor: "transparent",
  },
  "&.cm-editor .cm-gutters": {
    backgroundColor: "color-mix(in srgb, var(--bg-0) 94%, var(--fg-3) 6%)",
    borderRight: "none",
  },
  // Diagnostics stay visible inline and in the issues panel. Their tiny
  // gutter marker, however, looks detached from the paper in visual mode.
  "&.cm-editor .cm-lintGutter": {
    display: "none !important",
  },
  "&.cm-editor .cm-lint-marker": {
    display: "none !important",
  },

  // Headings
  ".cm-vis-heading": {
    fontWeight: "700",
    color: "var(--fg-0)",
    letterSpacing: "-0.015em",
  },
  ".cm-vis-h1": { fontSize: "1.65em" },
  ".cm-vis-h2": { fontSize: "1.4em" },
  ".cm-vis-h3": { fontSize: "1.18em" },
  ".cm-vis-h4": { fontSize: "1.06em" },
  ".cm-vis-h5": { fontSize: "1em", fontStyle: "italic" },
  ".cm-vis-heading-line": {
    paddingTop: "0.7em",
    paddingBottom: "0.2em",
  },
  ".cm-vis-align-left": { textAlign: "left" },
  ".cm-vis-align-center": { textAlign: "center" },
  ".cm-vis-align-right": { textAlign: "right" },

  // Protected generated includes (e.g. approval + title pages). This is an
  // honest structural placeholder; exact page pixels belong to the PDF view.
  ".cm-vis-embedded-input": {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    minHeight: "86px",
    margin: "8px 0 18px",
    padding: "12px 14px",
    gap: "13px",
    border: "1px solid var(--accent-line)",
    borderRadius: "10px",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, var(--bg-1)), var(--bg-1) 58%)",
    boxShadow: "0 8px 24px color-mix(in srgb, #000 9%, transparent)",
    color: "var(--fg-0)",
    fontFamily: "var(--font-sans)",
    lineHeight: "1.25",
    userSelect: "none",
  },
  ".cm-vis-embedded-input-preview": {
    position: "relative",
    display: "inline-block",
    width: "48px",
    height: "58px",
    flex: "0 0 48px",
  },
  ".cm-vis-embedded-input-preview > span": {
    position: "absolute",
    boxSizing: "border-box",
    width: "36px",
    height: "50px",
    border: "1px solid var(--border-strong)",
    borderRadius: "3px",
    background: "var(--bg-0)",
    boxShadow: "var(--shadow-1)",
  },
  ".cm-vis-embedded-input-preview > span:first-child": {
    left: "0",
    top: "7px",
    opacity: "0.7",
  },
  ".cm-vis-embedded-input-preview > span:last-child": {
    left: "10px",
    top: "0",
    borderColor: "var(--accent-line)",
  },
  ".cm-vis-embedded-input-preview > span:last-child::after": {
    content: '""',
    position: "absolute",
    inset: "12px 7px auto",
    height: "2px",
    background: "var(--accent-line)",
    boxShadow: "0 7px 0 var(--border), 0 14px 0 var(--border)",
  },
  ".cm-vis-embedded-input-copy": {
    display: "flex",
    minWidth: "0",
    flex: "1 1 190px",
    flexDirection: "column",
    gap: "3px",
  },
  ".cm-vis-embedded-input-copy strong": {
    fontSize: "13px",
    fontWeight: "700",
  },
  ".cm-vis-embedded-input-copy > span": {
    color: "var(--fg-2)",
    fontSize: "11px",
  },
  ".cm-vis-embedded-input-copy code": {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "9.5px",
  },
  ".cm-vis-embedded-input-status": {
    flexShrink: "0",
    padding: "4px 7px",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontSize: "9.5px",
    whiteSpace: "nowrap",
  },
  ".cm-vis-embedded-input-open": {
    minHeight: "34px",
    flexShrink: "0",
    padding: "0 10px",
    border: "1px solid var(--accent-line)",
    borderRadius: "7px",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontFamily: "var(--font-sans)",
    fontSize: "10.5px",
    fontWeight: "600",
    cursor: "pointer",
  },
  ".cm-vis-embedded-input-open:hover": {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 17%, transparent)",
  },
  ".cm-vis-embedded-input-open:focus-visible": {
    outline: "2px solid var(--accent)",
    outlineOffset: "2px",
  },

  // Shared shell for semantic, source-backed input previews.
  ".cm-vis-source-block": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "0",
    margin: "10px 0 22px",
    border: "1px solid var(--accent-line)",
    borderRadius: "10px",
    background: "var(--bg-1)",
    boxShadow: "0 8px 24px color-mix(in srgb, #000 9%, transparent)",
    overflow: "hidden",
    color: "var(--fg-0)",
  },
  ".cm-vis-source-toolbar": {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px",
    padding: "11px 13px",
    borderBottom: "1px solid var(--border)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    lineHeight: "1.25",
    userSelect: "none",
  },
  ".cm-vis-source-copy": {
    display: "flex",
    minWidth: "150px",
    flex: "1 1 220px",
    flexDirection: "column",
    gap: "2px",
  },
  ".cm-vis-source-copy strong": { fontSize: "12.5px", fontWeight: "700" },
  ".cm-vis-source-copy > span": { color: "var(--fg-2)", fontSize: "10.5px" },
  ".cm-vis-source-copy code": {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
  },
  ".cm-vis-source-status": {
    flexShrink: "0",
    padding: "4px 7px",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "9.5px",
    whiteSpace: "nowrap",
  },
  ".cm-vis-source-open": {
    minHeight: "34px",
    flexShrink: "0",
    padding: "0 10px",
    border: "1px solid var(--accent-line)",
    borderRadius: "7px",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontFamily: "var(--font-sans)",
    fontSize: "10.5px",
    fontWeight: "600",
    cursor: "pointer",
  },
  ".cm-vis-source-open:hover": {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 17%, transparent)",
  },
  ".cm-vis-source-open:focus-visible": {
    outline: "2px solid var(--accent)",
    outlineOffset: "2px",
  },
  ".cm-vis-source-notice": {
    padding: "7px 13px",
    borderBottom: "1px solid var(--border)",
    background: "color-mix(in srgb, var(--accent) 4%, var(--bg-1))",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "9.5px",
    lineHeight: "1.35",
  },
  ".cm-vis-generic-snippets": {
    display: "grid",
    gap: "7px",
    padding: "12px 13px 14px",
    background: "var(--bg-1)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
  },
  ".cm-vis-generic-snippet": {
    padding: "8px 10px",
    borderLeft: "2px solid var(--accent-line)",
    borderRadius: "0 5px 5px 0",
    background: "color-mix(in srgb, var(--fg-3) 4%, transparent)",
    color: "var(--fg-0)",
    fontSize: "11px",
    lineHeight: "1.4",
  },
  ".cm-vis-generic-empty": {
    padding: "16px 10px",
    color: "var(--fg-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "10.5px",
    fontStyle: "italic",
    textAlign: "center",
  },
  ".cm-vis-title-pages": {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(270px, 100%), 1fr))",
    alignItems: "start",
    gap: "14px",
    padding: "16px",
    background: "color-mix(in srgb, var(--fg-3) 7%, var(--bg-2))",
  },
  ".cm-vis-title-block.is-vkr .cm-vis-title-pages": {
    display: "block",
  },
  ".cm-vis-title-page": {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    aspectRatio: "210 / 297",
    minHeight: "420px",
    padding: "28px 24px 24px 34px",
    border: "1px solid #d7d9de",
    borderRadius: "2px",
    background: "#fff",
    boxShadow: "0 3px 12px rgba(17, 24, 39, 0.11)",
    overflow: "hidden",
    color: "#17191d",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
    fontSize: "8.5px",
    lineHeight: "1.25",
    whiteSpace: "normal",
  },
  ".cm-vis-title-approval::before, .cm-vis-title-main::before": {
    content:
      '"Подп. и дата · Инв. № дубл. · Взам. инв. № · Подп. и дата · Инв. № подл."',
    position: "absolute",
    inset: "22px auto 22px 8px",
    display: "grid",
    placeItems: "center",
    width: "15px",
    border: "1px solid #6b7280",
    fontSize: "5.5px",
    lineHeight: "1",
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
  },
  ".cm-vis-title-page-badge": {
    position: "absolute",
    top: "7px",
    right: "8px",
    color: "#8a8f99",
    fontFamily: "var(--font-sans)",
    fontSize: "6.5px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  ".cm-vis-title-institution": {
    fontWeight: "700",
    lineHeight: "1.35",
    textAlign: "center",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-signatures": {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: "16px",
    marginTop: "20px",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-signature-heading": { marginBottom: "5px", fontWeight: "700" },
  ".cm-vis-title-signature-line": {
    height: "8px",
    margin: "3px 0",
    borderBottom: "1px solid #4b5563",
  },
  ".cm-vis-title-secondary": { marginTop: "8px" },
  ".cm-vis-title-work": {
    marginTop: "32px",
    textAlign: "center",
  },
  ".cm-vis-title-project": {
    margin: "8px 0",
    fontSize: "13px",
    fontWeight: "700",
    lineHeight: "1.2",
  },
  ".cm-vis-title-document": { fontSize: "11px", fontWeight: "700" },
  ".cm-vis-title-main-label": { marginTop: "4px", fontWeight: "700" },
  ".cm-vis-title-code": { marginTop: "5px", fontWeight: "700" },
  ".cm-vis-title-executor": {
    position: "absolute",
    right: "24px",
    bottom: "24px",
    maxWidth: "48%",
    textAlign: "left",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-approved": {
    textAlign: "center",
    fontWeight: "700",
    whiteSpace: "pre-line",
  },
  ".cm-vis-title-page-count": { marginTop: "24px", color: "#555b66" },
  ".cm-vis-title-year": {
    position: "absolute",
    right: "24px",
    bottom: "20px",
    left: "34px",
    textAlign: "center",
    fontWeight: "700",
  },
  ".cm-vis-vkr-title-page": {
    maxWidth: "520px",
    minHeight: "690px",
    margin: "0 auto",
    padding: "36px 42px 28px",
    fontSize: "10px",
  },
  ".cm-vis-vkr-meta": {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "start",
    gap: "30px",
    marginTop: "15px",
  },
  ".cm-vis-vkr-approval": { textAlign: "center", whiteSpace: "pre-line" },
  ".cm-vis-vkr-title-page .cm-vis-title-work": { marginTop: "36px" },
  ".cm-vis-title-topic-label": { marginTop: "12px" },
  ".cm-vis-vkr-people": { marginTop: "38px", gap: "34px" },
  ".cm-vis-vkr-cosupervisor": {
    width: "45%",
    marginTop: "24px",
    whiteSpace: "pre-line",
  },

  // Source-backed longtable shared by TZ and PMI.
  ".cm-vis-requirements-warning": {
    padding: "7px 13px",
    borderBottom: "1px solid var(--border)",
    background: "color-mix(in srgb, var(--c-warn) 9%, var(--bg-1))",
    color: "var(--fg-1)",
    fontFamily: "var(--font-sans)",
    fontSize: "9.5px",
    lineHeight: "1.35",
  },
  ".cm-vis-requirements-warning::before": {
    content: '"!"',
    display: "inline-grid",
    placeItems: "center",
    width: "15px",
    height: "15px",
    marginRight: "6px",
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--c-warn) 18%, transparent)",
    color: "var(--c-warn)",
    fontSize: "9px",
    fontWeight: "800",
  },
  ".cm-vis-requirements-page": {
    boxSizing: "border-box",
    minWidth: "0",
    padding: "18px 15px 16px",
    background: "var(--bg-1)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
  },
  ".cm-vis-requirements-caption": {
    margin: "0 0 10px",
    color: "var(--fg-0)",
    fontSize: "10.5px",
    lineHeight: "1.3",
    textAlign: "left",
  },
  ".cm-vis-requirements-scroll": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overscrollBehaviorInline: "contain",
    scrollbarWidth: "thin",
  },
  ".cm-vis-requirements-table": {
    width: "100%",
    minWidth: "620px",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    color: "var(--fg-0)",
    fontSize: "9px",
    lineHeight: "1.25",
  },
  ".cm-vis-requirements-table th, .cm-vis-requirements-table td": {
    boxSizing: "border-box",
    border: "1px solid var(--fg-2)",
    padding: "7px 6px",
    textAlign: "left",
    verticalAlign: "top",
    overflowWrap: "anywhere",
  },
  ".cm-vis-requirements-table th": {
    background: "color-mix(in srgb, var(--fg-3) 7%, transparent)",
    fontWeight: "700",
    textAlign: "center",
    verticalAlign: "middle",
  },
  ".cm-vis-requirements-table td:first-child, .cm-vis-requirements-table td:last-child":
    {
      textAlign: "center",
    },
  ".cm-vis-requirements-table td:first-child": {
    color: "var(--accent)",
    fontFamily: "var(--font-mono)",
    fontSize: "8px",
    fontWeight: "650",
  },
  ".cm-vis-requirements-empty": {
    height: "58px",
    color: "var(--fg-3)",
    fontFamily: "var(--font-sans)",
    fontStyle: "italic",
    textAlign: "center !important",
    verticalAlign: "middle !important",
  },

  // Source-backed shared registration sheet. The table is semantic and
  // scrolls inside its own page on narrow screens, never widening CodeMirror.
  ".cm-vis-change-log": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "0",
    margin: "10px 0 22px",
    border: "1px solid var(--accent-line)",
    borderRadius: "10px",
    background: "var(--bg-1)",
    boxShadow: "0 8px 24px color-mix(in srgb, #000 9%, transparent)",
    overflow: "hidden",
    color: "var(--fg-0)",
  },
  ".cm-vis-change-log-toolbar": {
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px",
    padding: "11px 13px",
    borderBottom: "1px solid var(--border)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 9%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    lineHeight: "1.25",
    userSelect: "none",
  },
  ".cm-vis-change-log-copy": {
    display: "flex",
    minWidth: "150px",
    flex: "1 1 220px",
    flexDirection: "column",
    gap: "2px",
  },
  ".cm-vis-change-log-copy strong": {
    fontSize: "12.5px",
    fontWeight: "700",
  },
  ".cm-vis-change-log-copy > span": {
    color: "var(--fg-2)",
    fontSize: "10.5px",
  },
  ".cm-vis-change-log-copy code": {
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
  },
  ".cm-vis-change-log-status": {
    flexShrink: "0",
    padding: "4px 7px",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "9.5px",
    whiteSpace: "nowrap",
  },
  ".cm-vis-change-log-open": {
    minHeight: "34px",
    flexShrink: "0",
    padding: "0 10px",
    border: "1px solid var(--accent-line)",
    borderRadius: "7px",
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontFamily: "var(--font-sans)",
    fontSize: "10.5px",
    fontWeight: "600",
    cursor: "pointer",
  },
  ".cm-vis-change-log-open:hover": {
    borderColor: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 17%, transparent)",
  },
  ".cm-vis-change-log-open:focus-visible": {
    outline: "2px solid var(--accent)",
    outlineOffset: "2px",
  },
  ".cm-vis-change-log-warning": {
    padding: "7px 13px",
    borderBottom: "1px solid var(--border)",
    background: "color-mix(in srgb, var(--c-warn) 9%, var(--bg-1))",
    color: "var(--fg-1)",
    fontFamily: "var(--font-sans)",
    fontSize: "9.5px",
    lineHeight: "1.35",
  },
  ".cm-vis-change-log-warning::before": {
    content: '"!"',
    display: "inline-grid",
    placeItems: "center",
    width: "15px",
    height: "15px",
    marginRight: "6px",
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--c-warn) 18%, transparent)",
    color: "var(--c-warn)",
    fontSize: "9px",
    fontWeight: "800",
  },
  ".cm-vis-change-log-page": {
    boxSizing: "border-box",
    minWidth: "0",
    padding: "18px 15px 16px",
    background: "var(--bg-1)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
  },
  ".cm-vis-change-log-page-header": {
    display: "flex",
    minHeight: "28px",
    flexDirection: "column",
    alignItems: "center",
    color: "var(--fg-1)",
    fontSize: "8.5px",
    lineHeight: "1.25",
  },
  ".cm-vis-change-log-page-header > span:first-child": {
    fontSize: "9.5px",
  },
  ".cm-vis-change-log-page-title": {
    margin: "10px 0 18px",
    textAlign: "center",
    fontSize: "13.5px",
    fontWeight: "700",
    letterSpacing: "0.01em",
  },
  ".cm-vis-change-log-table-scroll": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overscrollBehaviorInline: "contain",
    scrollbarWidth: "thin",
  },
  ".cm-vis-change-log-table": {
    width: "100%",
    minWidth: "620px",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    color: "var(--fg-0)",
    fontSize: "7.5px",
    lineHeight: "1.12",
  },
  ".cm-vis-change-log-table th, .cm-vis-change-log-table td": {
    boxSizing: "border-box",
    border: "1px solid var(--fg-2)",
    padding: "2px 2px",
    textAlign: "center",
    verticalAlign: "middle",
    overflowWrap: "anywhere",
  },
  ".cm-vis-change-log-table th": {
    height: "30px",
    background: "color-mix(in srgb, var(--fg-3) 5%, transparent)",
    fontWeight: "400",
  },
  ".cm-vis-change-log-table thead tr:first-child th": {
    height: "24px",
    fontSize: "8.5px",
    fontWeight: "600",
  },
  ".cm-vis-change-log-table tbody td": {
    height: "20px",
  },

  // Inline formatting
  ".cm-vis-b": { fontWeight: "700" },
  ".cm-vis-i": { fontStyle: "italic" },
  ".cm-vis-u": { textDecoration: "underline" },
  ".cm-vis-tt": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.92em",
    background: "var(--bg-2)",
    borderRadius: "3px",
    padding: "0 3px",
  },
  ".cm-vis-inline-code": {
    padding: "1px 4px",
    border: "1px solid var(--border)",
    borderRadius: "4px",
    background: "var(--bg-2)",
    color: "var(--fg-1)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.88em",
    cursor: "text",
  },
  ".cm-vis-sc": { fontVariant: "small-caps" },
  ".cm-vis-sup": { verticalAlign: "super", fontSize: "0.8em" },
  ".cm-vis-sub": { verticalAlign: "sub", fontSize: "0.8em" },
  ".cm-vis-strike": { textDecoration: "line-through" },
  ".cm-vis-comment": {
    color: "var(--fg-3)",
    fontStyle: "italic",
    opacity: "0.9",
    fontSize: "0.9em",
  },

  // List bullets
  ".cm-vis-bullet": {
    display: "inline-block",
    minWidth: "1.35em",
    color: "var(--fg-1)",
  },
  ".cm-vis-bullet-description": { color: "var(--fg-0)" },

  // Preamble banner
  ".cm-vis-preamble": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "0 0 6px",
    padding: "5px 12px",
    background: "var(--bg-1)",
    borderBottom: "1px solid var(--border)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "11.5px",
    cursor: "pointer",
    userSelect: "none",
  },
  ".cm-vis-preamble:hover": { color: "var(--fg-0)" },
  ".cm-vis-preamble-chevron": { color: "var(--fg-3)" },

  // Math widgets
  ".cm-vis-math": { cursor: "pointer" },
  ".cm-vis-math-pending": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.9em",
    color: "var(--fg-2)",
  },
  ".cm-vis-math-error": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.9em",
    color: "var(--c-err)",
  },
  ".cm-vis-math-display": {
    textAlign: "center",
    padding: "6px 0",
  },

  // Reference / citation / label / graphic pills
  ".cm-vis-pill": {
    display: "inline-block",
    padding: "0 6px",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-1)",
    fontSize: "0.85em",
    lineHeight: "1.5",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  ".cm-vis-pill:hover": { borderColor: "var(--accent-line)" },
  ".cm-vis-pill-label": { opacity: "0.7" },
  ".cm-vis-pill-link": {
    color: "var(--accent)",
    borderColor: "color-mix(in srgb, var(--accent) 35%, var(--border))",
  },
  ".cm-vis-pill-req": {
    color: "var(--accent)",
    background: "var(--accent-soft)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.78em",
  },
  ".cm-vis-pill-todo": {
    color: "#8a5a00",
    background: "color-mix(in srgb, #f5a623 22%, transparent)",
    borderColor: "color-mix(in srgb, #f5a623 55%, transparent)",
    fontWeight: "600",
  },
  ".cm-vis-pill-todo:hover": {
    borderColor: "#f5a623",
  },
  ".cm-vis-pill-note": {
    verticalAlign: "super",
    fontSize: "0.7em",
    padding: "0 4px",
    color: "var(--accent)",
  },

  // beamer slide / block header (\begin{frame}{Title} → a titled card head).
  ".cm-vis-slide-head": {
    display: "inline-flex",
    alignItems: "center",
    gap: "9px",
    width: "100%",
    boxSizing: "border-box",
    margin: "10px 0 2px",
    padding: "7px 12px",
    borderRadius: "9px 9px 0 0",
    border: "1px solid var(--border-strong)",
    borderBottom: "2px solid var(--accent-line)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 12%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    userSelect: "none",
  },
  ".cm-vis-slide-icon": { flexShrink: "0", fontSize: "14px", lineHeight: "1" },
  ".cm-vis-slide-title": {
    flex: "1 1 auto",
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "var(--fg-0)",
    fontSize: "14px",
    fontWeight: "700",
  },
  ".cm-vis-slide-badge": {
    flexShrink: "0",
    padding: "1px 9px",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-3)",
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "nowrap",
  },

  // `\captionof{…}{text}` shown as a muted caption line.
  ".cm-vis-caption": {
    color: "var(--fg-2)",
    fontStyle: "italic",
    fontSize: "0.92em",
  },

  // `\hrulefill`/`\dotfill` shown as a fill-in blank line.
  ".cm-vis-formblank": {
    display: "inline-block",
    minWidth: "140px",
    borderBottom: "1px solid var(--fg-3)",
    verticalAlign: "baseline",
  },

  // Bibliography (thebibliography) inline list: a titled header + «Управлять»,
  // a numbered badge with a citation-key chip per \bibitem, a «+ Добавить» foot.
  ".cm-vis-bib-head": {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    margin: "6px 0 2px",
    padding: "3px 4px 8px",
    borderBottom: "2px solid var(--accent-line)",
    fontFamily: "var(--font-sans)",
    userSelect: "none",
  },
  ".cm-vis-bib-head-icon": { fontSize: "14px", lineHeight: "1" },
  ".cm-vis-bib-head-label": {
    color: "var(--fg-0)",
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "0.01em",
  },
  ".cm-vis-bib-manage, .cm-vis-bib-add": {
    padding: "2px 9px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg-2)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-sans)",
    fontSize: "11px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "border-color 120ms var(--ease), color 120ms var(--ease)",
  },
  ".cm-vis-bib-manage:hover, .cm-vis-bib-add:hover": {
    borderColor: "var(--accent-line)",
    color: "var(--accent)",
  },
  ".cm-vis-bib-foot": {
    display: "inline-flex",
    margin: "4px 0 8px",
    userSelect: "none",
  },
  ".cm-vis-bib-add": {
    color: "var(--accent)",
    background: "var(--accent-soft)",
  },
  ".cm-vis-bibitem": {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "6px",
    marginRight: "6px",
    verticalAlign: "baseline",
    userSelect: "none",
  },
  ".cm-vis-bibitem-num": {
    color: "var(--accent)",
    fontWeight: "700",
    fontVariantNumeric: "tabular-nums",
  },
  ".cm-vis-bibitem-key": {
    padding: "0 6px",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.78em",
    lineHeight: "1.5",
    cursor: "pointer",
  },
  ".cm-vis-bibitem-key:hover": {
    borderColor: "var(--accent-line)",
    color: "var(--accent)",
  },

  // Auto-generated document list (\hseManualDocItems → the manuals in the set):
  // a read-only card driven by the «Руководства в комплекте» setting.
  ".cm-vis-manuals": {
    display: "inline-flex",
    flexDirection: "column",
    gap: "5px",
    margin: "2px 0",
    padding: "9px 13px",
    border: "1px dashed var(--accent-line)",
    borderRadius: "9px",
    background: "var(--accent-soft)",
    fontFamily: "var(--font-sans)",
    fontSize: "0.92em",
    lineHeight: "1.4",
    cursor: "pointer",
    userSelect: "none",
    transition: "border-color 120ms var(--ease)",
  },
  ".cm-vis-manuals:hover": { borderColor: "var(--accent)" },
  ".cm-vis-manuals-badge": {
    alignSelf: "flex-start",
    padding: "1px 8px",
    borderRadius: "999px",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    color: "var(--fg-3)",
    fontSize: "0.66em",
    fontWeight: "700",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  ".cm-vis-manuals-list": {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },
  ".cm-vis-manuals-item": {
    display: "flex",
    alignItems: "baseline",
    gap: "7px",
    color: "var(--fg-1)",
  },
  ".cm-vis-manuals-icon": { flexShrink: "0", fontSize: "0.9em" },

  // Framed raw environments (prose / non-full-line fallback)
  ".cm-vis-envframe": {
    borderLeft: "2px solid var(--accent-line)",
    paddingLeft: "10px",
    background: "color-mix(in srgb, var(--bg-2) 45%, transparent)",
    fontFamily: "var(--font-mono)",
    fontSize: "0.86em",
  },
  ".cm-vis-envname": { color: "var(--accent)", opacity: "0.8" },

  // Inline block cards: a whole table / figure / non-prose environment shown
  // as an editable card. The whole card reveals the raw LaTeX on click, the
  // same escape hatch as formulas. One shared shell, three bodies.
  ".cm-vis-card": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: "0",
    margin: "12px 0 20px",
    border: "1px solid var(--border-strong)",
    borderRadius: "10px",
    background: "var(--bg-1)",
    boxShadow: "0 6px 20px color-mix(in srgb, #000 8%, transparent)",
    overflow: "hidden",
    color: "var(--fg-0)",
    cursor: "pointer",
    transition: "border-color 140ms var(--ease), box-shadow 140ms var(--ease)",
  },
  ".cm-vis-card:hover": {
    borderColor: "var(--accent-line)",
    boxShadow: "0 8px 26px color-mix(in srgb, var(--accent) 12%, transparent)",
  },
  ".cm-vis-card-head": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderBottom: "1px solid var(--border)",
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--accent) 8%, var(--bg-1)), var(--bg-1) 62%)",
    fontFamily: "var(--font-sans)",
    userSelect: "none",
  },
  ".cm-vis-card-icon": { flexShrink: "0", fontSize: "13px", lineHeight: "1" },
  ".cm-vis-card-label": {
    flex: "1 1 auto",
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "var(--fg-0)",
    fontSize: "12.5px",
    fontWeight: "700",
  },
  ".cm-vis-card-hint": {
    flexShrink: "0",
    color: "var(--fg-3)",
    fontSize: "10px",
    whiteSpace: "nowrap",
  },
  ".cm-vis-card-code": {
    flexShrink: "0",
    padding: "2px 7px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg-2)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "10px",
    cursor: "pointer",
  },
  ".cm-vis-card-code:hover": {
    borderColor: "var(--accent-line)",
    color: "var(--accent)",
  },
  ".cm-vis-card-empty": {
    padding: "16px 14px",
    color: "var(--fg-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "11px",
    fontStyle: "italic",
    textAlign: "center",
  },
  ".cm-vis-doc-table-scroll": {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    padding: "12px 14px 14px",
    overflowX: "auto",
    overscrollBehaviorInline: "contain",
    scrollbarWidth: "thin",
    background: "var(--bg-1)",
  },
  ".cm-vis-doc-table-grid": {
    width: "100%",
    borderCollapse: "collapse",
    color: "var(--fg-0)",
    fontFamily: '"Times New Roman", "PT Serif", Georgia, serif',
    fontSize: "12px",
    lineHeight: "1.3",
  },
  ".cm-vis-doc-table-grid th, .cm-vis-doc-table-grid td": {
    boxSizing: "border-box",
    border: "1px solid var(--fg-2)",
    padding: "6px 8px",
    textAlign: "left",
    verticalAlign: "top",
    overflowWrap: "anywhere",
  },
  ".cm-vis-doc-table-grid th": {
    background: "color-mix(in srgb, var(--fg-3) 8%, transparent)",
    fontWeight: "700",
    textAlign: "center",
    verticalAlign: "middle",
  },
  ".cm-vis-figure-body": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "14px",
    background: "var(--bg-1)",
  },
  ".cm-vis-figure-body img": {
    maxWidth: "100%",
    maxHeight: "320px",
    objectFit: "contain",
    borderRadius: "4px",
  },
  ".cm-vis-figure-name": {
    maxWidth: "100%",
    overflow: "hidden",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "10px",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  ".cm-vis-figure-broken img": { display: "none" },
  ".cm-vis-figure-broken .cm-vis-figure-name": { color: "var(--c-warn)" },
  ".cm-vis-figure-empty": {
    display: "grid",
    placeItems: "center",
    width: "100%",
    minHeight: "120px",
    padding: "12px",
    border: "1px dashed var(--border-strong)",
    borderRadius: "6px",
    background: "color-mix(in srgb, var(--fg-3) 4%, transparent)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: "11px",
    textAlign: "center",
  },
  ".cm-vis-envcard-peek": {
    maxHeight: "88px",
    padding: "10px 14px 12px",
    background: "var(--bg-1)",
    color: "var(--fg-2)",
    fontFamily: "var(--font-mono)",
    fontSize: "11px",
    lineHeight: "1.45",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    overflowWrap: "anywhere",
  },

  // Word-style fields: resolved macro values (\hseProjectName → its value)
  ".cm-vis-field": {
    backgroundColor: "color-mix(in srgb, var(--accent) 13%, transparent)",
    boxShadow:
      "inset 0 -1px 0 color-mix(in srgb, var(--accent) 35%, transparent)",
    borderRadius: "3px",
    padding: "0 3px",
    cursor: "pointer",
  },
  ".cm-vis-field:hover": {
    backgroundColor: "color-mix(in srgb, var(--accent) 22%, transparent)",
  },

  // «Заполните сами» — mirrors the pack's yellow \hseFill boxes in the PDF
  ".cm-vis-fill": {
    backgroundColor: "color-mix(in srgb, #f2c94c 32%, transparent)",
    color: "var(--fg-1)",
    fontStyle: "italic",
    borderRadius: "3px",
    padding: "0 3px",
    cursor: "pointer",
  },
  ".cm-vis-fill:hover": {
    backgroundColor: "color-mix(in srgb, #f2c94c 48%, transparent)",
  },

  // No-prose setup commands (\addcontentsline, \medskip, \tableofcontents…)
  ".cm-vis-setup": {
    fontFamily: "var(--font-mono)",
    fontSize: "0.72em",
    color: "var(--fg-3)",
    backgroundColor: "var(--bg-2)",
    border: "1px solid var(--border)",
    borderRadius: "999px",
    padding: "1px 7px",
    verticalAlign: "middle",
    opacity: "0.8",
    cursor: "pointer",
  },

  // Collapsed run of full-line comments
  ".cm-vis-commentblock": {
    color: "var(--fg-3)",
    fontSize: "11px",
    fontStyle: "italic",
    opacity: "0.7",
    cursor: "pointer",
    userSelect: "none",
    padding: "1px 0",
  },

  // «Подсказка:» hint callouts
  ".cm-vis-hint": {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    margin: "4px 0",
    padding: "6px 10px",
    borderLeft: "3px solid color-mix(in srgb, var(--accent) 55%, transparent)",
    background: "color-mix(in srgb, var(--accent) 7%, transparent)",
    borderRadius: "0 6px 6px 0",
    color: "var(--fg-2)",
    fontSize: "12px",
    fontStyle: "italic",
    lineHeight: "1.5",
    cursor: "pointer",
  },
  ".cm-vis-hint-icon": { flexShrink: "0", fontStyle: "normal" },
  ".cm-vis-hint-text": { flex: "1", minWidth: "0" },
  ".cm-vis-hint-close": {
    flexShrink: "0",
    border: "none",
    background: "transparent",
    color: "var(--fg-3)",
    cursor: "pointer",
    fontSize: "11px",
    padding: "0 2px",
    lineHeight: "1.4",
  },
  ".cm-vis-hint-close:hover": { color: "var(--c-err)" },

  // «Образец» blocks (hseExample): banner + tinted content lines
  ".cm-vis-example-banner": {
    margin: "6px 0 0",
    padding: "4px 10px",
    background: "color-mix(in srgb, #f2c94c 26%, transparent)",
    borderRadius: "6px 6px 0 0",
    color: "var(--fg-1)",
    fontSize: "11px",
    letterSpacing: "0.03em",
    cursor: "pointer",
    userSelect: "none",
  },
  ".cm-vis-example-line": {
    background: "color-mix(in srgb, #f2c94c 10%, transparent)",
    borderLeft: "3px solid color-mix(in srgb, #f2c94c 45%, transparent)",
    paddingLeft: "10px",
  },

  // Inline image previews (\includegraphics)
  ".cm-vis-img": {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
    height: "188px",
    maxWidth: "100%",
    padding: "6px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg-1)",
    cursor: "pointer",
    verticalAlign: "middle",
    overflow: "hidden",
  },
  ".cm-vis-img img": {
    flex: "1",
    minHeight: "0",
    maxWidth: "100%",
    objectFit: "contain",
  },
  ".cm-vis-img-name": {
    color: "var(--fg-3)",
    fontSize: "10px",
    fontFamily: "var(--font-mono)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  ".cm-vis-img-broken img": { display: "none" },
  ".cm-vis-img-broken .cm-vis-img-name": { color: "var(--c-warn)" },

  // Page divider (\newpage etc.)
  ".cm-vis-divider": {
    display: "inline-flex",
    alignItems: "center",
    width: "100%",
    gap: "8px",
    color: "var(--fg-3)",
    fontSize: "10px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: "pointer",
  },
  ".cm-vis-divider::before, .cm-vis-divider::after": {
    content: '""',
    flex: "1",
    borderTop: "1px dashed var(--border)",
  },

  // `\\` glyph
  ".cm-vis-linebreak": {
    color: "var(--fg-3)",
    fontSize: "0.85em",
    cursor: "pointer",
  },
});
